import React from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import Input from '../elements/form/input';

interface CustomPhoneInputProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
}

export default function CustomPhoneInput<T extends FieldValues>({
  name,
  control,
  label = "Phone Number",
  placeholder = "Enter US phone number (e.g., 555-123-4567)",
  required = false,
  className = "!my-0 !mb-6",
  disabled = false
}: CustomPhoneInputProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? "Phone number is required" : false,
        pattern: {
          value: /^(\+1|1)?[-.\s]?(\([0-9]\d{2}\)|[0-9]\d{2})[-.\s]?[0-9]\d{2}[-.\s]?\d{4}$/,
          message: "Please enter a valid US phone number",
        },
        validate: (value) => {
          if (!required && !value) return true; // Allow empty if not required
          // Remove all non-numeric characters to check length
          const numbersOnly = value.replace(/\D/g, '');
          if (numbersOnly.length === 10) return true;
          if (numbersOnly.length === 11 && numbersOnly.startsWith('1')) return true;
          return "Phone number must be 10 digits or 11 digits starting with 1";
        }
      }}
      render={({ field, fieldState }) => (
        <Input
          {...field}
          className={className}
          type="tel"
          label={label}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          error={fieldState.error?.message}
          onChange={(e) => {
            let value = e.target.value;
            
            // Remove all non-numeric characters except + and 1 at start
            let numbersOnly = value.replace(/\D/g, '');
            
            // Handle +1 prefix
            if (value.startsWith('+1')) {
              numbersOnly = numbersOnly.substring(1); // Remove leading 1 from numbersOnly
            } else if (value.startsWith('1') && numbersOnly.length === 11) {
              numbersOnly = numbersOnly.substring(1); // Remove leading 1 if it makes 11 digits
            }
            
            // Limit to 10 digits for US format
            if (numbersOnly.length > 10) {
              numbersOnly = numbersOnly.substring(0, 10);
            }
            
            // Format as (XXX) XXX-XXXX
            let formattedValue = '';
            if (numbersOnly.length > 0) {
              if (numbersOnly.length <= 3) {
                formattedValue = `(${numbersOnly}`;
              } else if (numbersOnly.length <= 6) {
                formattedValue = `(${numbersOnly.substring(0, 3)}) ${numbersOnly.substring(3)}`;
              } else {
                formattedValue = `(${numbersOnly.substring(0, 3)}) ${numbersOnly.substring(3, 6)}-${numbersOnly.substring(6)}`;
              }
            }
            
            // Add +1 prefix if original value had it
            if (value.startsWith('+1') && formattedValue) {
              formattedValue = '+1 ' + formattedValue;
            }
            
            field.onChange(formattedValue);
          }}
        />
      )}
    />
  );
}

// Utility function to format phone number when loading from API
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";
  
  // Remove all non-numeric characters
  let numbersOnly = phone.replace(/\D/g, '');
  
  // Handle country code if present
  if (numbersOnly.startsWith('1') && numbersOnly.length === 11) {
    numbersOnly = numbersOnly.substring(1); // Remove leading 1
  }
  
  // Only format if we have 10 digits
  if (numbersOnly.length === 10) {
    return `(${numbersOnly.substring(0, 3)}) ${numbersOnly.substring(3, 6)}-${numbersOnly.substring(6)}`;
  }
  
  // Return original if it doesn't match expected format
  return phone;
};

// Utility function to convert formatted phone number to plain string for API
export const getPhoneNumberForAPI = (formattedPhone: string): string => {
  if (!formattedPhone) return "";
  
  // Remove all non-numeric characters
  const numbersOnly = formattedPhone.replace(/\D/g, '');
  
  // Return just the numeric string (10 digits for US numbers)
  return numbersOnly;
};